import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Plus, Trash2, ChevronDown, ChevronUp, Sparkles, X, Check, AlertCircle, Loader2, Calculator, History } from 'lucide-react';
import ApiService from '../services/apiService';
import MarkdownText from '../components/ui/MarkdownText';
import { commitModule } from '../utils/reportMarkers';

// ── Debounce utility ────────────────────────────────────────────────────────
function useDebouncedCallback(callback, delay) {
  const timerRef = useRef({});
  return useCallback((key, ...args) => {
    if (timerRef.current[key]) clearTimeout(timerRef.current[key]);
    timerRef.current[key] = setTimeout(() => {
      callback(...args);
      delete timerRef.current[key];
    }, delay);
  }, [callback, delay]);
}

// ── Format currency ─────────────────────────────────────────────────────────
function formatAmount(num, t) {
  if (num === null || num === undefined) return `0 ${t?.currencySymbol || '£E'}`;
  const abs = Math.abs(num);
  const formatted = abs >= 1000000
    ? `${(abs / 1000000).toFixed(1)}M`
    : abs >= 1000
      ? `${(abs / 1000).toFixed(1)}K`
      : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${num < 0 ? '-' : ''}${formatted} ${t?.currencySymbol || '£E'}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// TREE NODE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
function TreeNode({ node, children: childNodes, lang, t, onUpdate, onDelete, onAddChild, onCategorySelect, allNodes, depth = 0, path = [], hoveredPath = [], setHoveredPath }) {
  const [isDetailExpanded, setIsDetailExpanded] = useState(node.is_expanded || false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryResults, setCategoryResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const deleteBtnRef = useRef(null);
  const [deleteConfirmStyle, setDeleteConfirmStyle] = useState({});

  const isRevenue = node.node_type === 'revenue';
  const displayName = lang === 'ar'
    ? (node.category_name_ar || node.custom_name || '')
    : (node.category_name_en || node.custom_name || '');

  // Compute sum from children if this node has children
  const childSum = useMemo(() => {
    if (!childNodes || childNodes.length === 0) return null;
    return childNodes.reduce((sum, c) => sum + (c.total_amount || 0), 0);
  }, [childNodes]);

  // Close dropdown on outside click or scroll
  useEffect(() => {
    const handleClick = (e) => {
      // If clicking inside the portal dropdown, ignore (handled by onClick in portal)
      const portal = document.getElementById('dropdown-portal-root');
      if (portal && portal.contains(e.target)) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      const deletePortal = document.getElementById('delete-portal-root');
      if (deletePortal && deletePortal.contains(e.target)) return;
      if (deleteBtnRef.current && !deleteBtnRef.current.contains(e.target)) {
        setShowDeleteConfirm(false);
      }
    };
    const handleScroll = () => {
      setShowDropdown(false);
      setShowDeleteConfirm(false);
    };
    
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999
      });
    }
  }, [showDropdown, categoryResults.length]);

  useEffect(() => {
    if (showDeleteConfirm && deleteBtnRef.current) {
      const rect = deleteBtnRef.current.getBoundingClientRect();
      const popupWidth = 180;
      let left = lang === 'ar' ? rect.left : rect.right - popupWidth;
      
      if (left < 10) left = 10;
      if (left + popupWidth > window.innerWidth - 10) left = window.innerWidth - popupWidth - 10;

      let top = rect.bottom + 8;
      if (top + 80 > window.innerHeight) {
        top = rect.top - 88;
      }

      setDeleteConfirmStyle({
        position: 'fixed',
        top,
        left,
        zIndex: 9999
      });
    }
  }, [showDeleteConfirm, lang]);

  // Category autocomplete search
  const searchCategories = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setCategoryResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const results = await ApiService.get(`/accounting/categories?q=${encodeURIComponent(query)}&node_type=${node.node_type}&lang=${lang}`);
      setCategoryResults(results || []);
    } catch (e) {
      console.error('Category search error:', e);
      setCategoryResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [node.node_type, lang]);

  // Handle category input change
  const handleCategoryInput = (val) => {
    setCategorySearch(val);
    setShowDropdown(true);
    // Debounced search
    const timeout = setTimeout(() => searchCategories(val), 300);
    return () => clearTimeout(timeout);
  };

  // Handle category selection from dropdown
  const handleSelectCategory = (cat) => {
    setShowDropdown(false);
    setCategorySearch('');
    onCategorySelect(node.id, cat);
  };

  // Handle auto-learn new category
  const handleCreateCategory = async () => {
    const newCat = {
      name_en: categorySearch,
      name_ar: categorySearch,
      node_type: node.node_type,
    };
    try {
      const result = await ApiService.post('/accounting/categories', newCat);
      if (result.id) {
        onCategorySelect(node.id, {
          id: result.id,
          name_en: categorySearch,
          name_ar: categorySearch,
          node_type: node.node_type,
        });
      }
    } catch (e) {
      console.error('Create category error:', e);
    }
    setShowDropdown(false);
    setCategorySearch('');
  };

  // Handle field changes with save status flash
  const handleFieldChange = (field, value) => {
    setSaveStatus('saving');
    const numVal = ['total_amount', 'unit_price', 'quantity'].includes(field) ? parseFloat(value) || 0 : value;

    // Auto-calc: if unit_price or quantity changes, recalculate total
    let updates = { [field]: numVal };
    if (field === 'unit_price') {
      updates.total_amount = numVal * (node.quantity || 0);
    } else if (field === 'quantity') {
      updates.total_amount = (node.unit_price || 0) * numVal;
    }

    onUpdate(node.id, updates, (success) => {
      setSaveStatus(success ? 'saved' : 'error');
      setTimeout(() => setSaveStatus(null), 1500);
    });
  };

  const frequencyOptions = [
    { value: 'daily', label: t.freq_daily },
    { value: 'weekly', label: t.freq_weekly },
    { value: 'monthly', label: t.freq_monthly },
    { value: 'seasonally', label: t.freq_seasonally },
    { value: 'yearly', label: t.freq_yearly },
  ];

  const currentPath = useMemo(() => [...path, node.id], [path, node.id]);
  const isHoveredPath = hoveredPath.includes(node.id);

  return (
    <div 
      className={`tree-connector ${isHoveredPath ? 'path-highlight' : ''}`} 
      style={{ marginBottom: '0.375rem', zIndex: showDropdown ? 50 : 1 }}
      onMouseEnter={() => setHoveredPath(currentPath)}
      onMouseLeave={() => setHoveredPath([])}
    >
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`tree-node-card ${isRevenue ? 'line-revenue node-revenue' : 'line-expense node-expense'} ${saveStatus === 'saved' ? 'save-flash' : ''} ${isHoveredPath ? 'ring-2 ring-opacity-50 ring-offset-2 ring-offset-[#F4EFEA] dark:ring-offset-[#0A0F1E] ' + (isRevenue ? 'ring-green-500' : 'ring-red-500') : ''}`}
      >
        {/* ── Top Row: Name + Amount + Actions ── */}
        <div className="flex items-center gap-2">
          {/* Category name / autocomplete */}
          <div className="flex-1 relative" ref={dropdownRef}>
            {displayName && !showDropdown ? (
              <div
                className="text-sm font-semibold cursor-pointer hover:opacity-70 transition-opacity truncate"
                onClick={() => {
                  setShowDropdown(true);
                  setCategorySearch(displayName);
                  setTimeout(() => inputRef.current?.focus(), 50);
                }}
                title={displayName}
              >
                {displayName}
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                className="acc-input"
                placeholder={t.categoryPlaceholder}
                value={categorySearch}
                onChange={(e) => handleCategoryInput(e.target.value)}
                onFocus={() => categorySearch && setShowDropdown(true)}
                autoFocus={!displayName}
              />
            )}
            {/* Autocomplete dropdown via Portal */}
            {showDropdown && createPortal(
              <div id="dropdown-portal-root" className="acc-dropdown" style={dropdownStyle} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                {searchLoading && (
                  <div className="acc-dropdown-item text-center opacity-50">
                    <Loader2 size={14} className="animate-spin inline" />
                  </div>
                )}
                {categoryResults.map(cat => (
                  <div
                    key={cat.id}
                    className="acc-dropdown-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectCategory(cat);
                    }}
                  >
                    {lang === 'ar' ? cat.name_ar : cat.name_en}
                  </div>
                ))}
                {categorySearch && categoryResults.length === 0 && !searchLoading && (
                  <div
                    className="acc-dropdown-item font-medium"
                    style={{ color: '#C47A2C' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCreateCategory();
                    }}
                  >
                    + {t.addNewCategory.replace('{name}', categorySearch)}
                  </div>
                )}
              </div>,
              document.body
            )}
          </div>

          {/* Amount display */}
          <div className={`acc-amount ${isRevenue ? 'acc-amount-revenue' : 'acc-amount-expense'} whitespace-nowrap`}>
            {childSum !== null ? formatAmount(childSum, t) : formatAmount(node.total_amount, t)}
          </div>

          {/* Save status indicator */}
          <AnimatePresence mode="wait">
            {saveStatus && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex-shrink-0"
              >
                {saveStatus === 'saving' && <Loader2 size={12} className="animate-spin text-amber-500" />}
                {saveStatus === 'saved' && <Check size={12} className="text-emerald-500" />}
                {saveStatus === 'error' && <AlertCircle size={12} className="text-red-500" />}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Expand/collapse details */}
          <button
            onClick={() => {
              setIsDetailExpanded(!isDetailExpanded);
              onUpdate(node.id, { is_expanded: !isDetailExpanded });
            }}
            className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex-shrink-0"
            title={isDetailExpanded ? t.collapseDetails : t.expandDetails}
          >
            {isDetailExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Add child */}
          <button
            onClick={() => onAddChild(node.id, node.node_type)}
            className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-[#C47A2C] flex-shrink-0"
            title={t.addChild}
          >
            <Plus size={14} />
          </button>

          {/* Delete */}
          <div className="relative flex-shrink-0" ref={deleteBtnRef}>
            <button
              onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
              className="p-1 rounded-lg hover:bg-red-500/10 transition-colors text-red-400/60 hover:text-red-500 flex-shrink-0"
              title={t.deleteNode}
            >
              <Trash2 size={13} />
            </button>
            {showDeleteConfirm && createPortal(
              <motion.div
                id="delete-portal-root"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={deleteConfirmStyle}
                className="bg-white dark:bg-[#1B263B] rounded-xl shadow-xl border border-red-500/20 p-3 min-w-[180px]"
              >
                <p className="text-xs mb-2 opacity-70">{t.deleteConfirm}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { onDelete(node.id); setShowDeleteConfirm(false); }}
                    className="flex-1 text-xs py-1.5 rounded-lg bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
                  >
                    {t.confirmYes}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 text-xs py-1.5 rounded-lg bg-black/5 dark:bg-white/5 font-medium hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                  >
                    {t.confirmNo}
                  </button>
                </div>
              </motion.div>,
              document.body
            )}
          </div>
        </div>

        {/* ── Expanded Detail View ── */}
        <AnimatePresence>
          {isDetailExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-black/5 dark:border-white/5">
                {/* Unit Price */}
                <div>
                  <label className="text-[0.65rem] font-semibold opacity-50 uppercase tracking-wide mb-1 block">{t.unitPrice}</label>
                  <input
                    type="number"
                    className="acc-input"
                    value={node.unit_price || ''}
                    onChange={(e) => handleFieldChange('unit_price', e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                {/* Quantity */}
                <div>
                  <label className="text-[0.65rem] font-semibold opacity-50 uppercase tracking-wide mb-1 block">{t.quantity}</label>
                  <input
                    type="number"
                    className="acc-input"
                    value={node.quantity || ''}
                    onChange={(e) => handleFieldChange('quantity', e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                {/* Frequency */}
                <div>
                  <label className="text-[0.65rem] font-semibold opacity-50 uppercase tracking-wide mb-1 block">{t.frequency}</label>
                  <select
                    className="acc-input"
                    value={node.frequency || ''}
                    onChange={(e) => handleFieldChange('frequency', e.target.value || null)}
                  >
                    <option value="">—</option>
                    {frequencyOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* If no children, show direct total input */}
              {(!childNodes || childNodes.length === 0) && (
                <div className="mt-2">
                  <label className="text-[0.65rem] font-semibold opacity-50 uppercase tracking-wide mb-1 block">{t.totalAmount}</label>
                  <input
                    type="number"
                    className="acc-input"
                    value={node.total_amount || ''}
                    onChange={(e) => handleFieldChange('total_amount', e.target.value)}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Children Nodes ── */}
      {childNodes && childNodes.length > 0 && (
        <div className="tree-children">
          <AnimatePresence>
            {childNodes.map(child => (
              <TreeNode
                key={child.id}
                node={child}
                children={allNodes.filter(n => n.parent_id === child.id)}
                lang={lang}
                t={t}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onAddChild={onAddChild}
                onCategorySelect={onCategorySelect}
                allNodes={allNodes}
                depth={depth + 1}
                path={currentPath}
                hoveredPath={hoveredPath}
                setHoveredPath={setHoveredPath}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MAIN ACCOUNTING VIEW
// ═══════════════════════════════════════════════════════════════════════════
export default function AccountingView({ userId, t: parentT, isRTL, lang, theme, onClose, setReportData }) {
  const t = parentT?.accounting || {};
  const BASE_URL = process.env.REACT_APP_API_URL || "/api";

  // ── State ─────────────────────────────────────────────────────────────
  const [nodes, setNodes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  // AI Advisor
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLength, setAiLength] = useState('medium');
  const [aiResponse, setAiResponse] = useState('');
  const [aiThinking, setAiThinking] = useState('');
  const [aiIsThinking, setAiIsThinking] = useState(false);
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiAbortControllerRef = useRef(null);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Tree Interactions
  const [hoveredPath, setHoveredPath] = useState([]);

  // ── Load tree from backend ────────────────────────────────────────────
  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ApiService.get(`/accounting/tree/${userId}?year=${selectedYear}`);
      setNodes(data.nodes || []);
    } catch (e) {
      console.error('Failed to load accounting tree:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedYear]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // ── Derived data ──────────────────────────────────────────────────────
  const rootRevenues = useMemo(() => nodes.filter(n => n.node_type === 'revenue' && !n.parent_id), [nodes]);
  const rootExpenses = useMemo(() => nodes.filter(n => n.node_type === 'expense' && !n.parent_id), [nodes]);

  // Recursive sum: for a given root array, sum up all totals
  const computeBranchTotal = useCallback((rootNodes) => {
    // For nodes with children, use the child sum; for leaves, use total_amount
    const getNodeTotal = (nodeId) => {
      const children = nodes.filter(n => n.parent_id === nodeId);
      if (children.length === 0) {
        const node = nodes.find(n => n.id === nodeId);
        return node?.total_amount || 0;
      }
      return children.reduce((sum, c) => sum + getNodeTotal(c.id), 0);
    };
    return rootNodes.reduce((sum, n) => sum + getNodeTotal(n.id), 0);
  }, [nodes]);

  const totalRevenue = useMemo(() => computeBranchTotal(rootRevenues), [rootRevenues, computeBranchTotal]);
  const totalExpenses = useMemo(() => computeBranchTotal(rootExpenses), [rootExpenses, computeBranchTotal]);
  const netProfit = totalRevenue - totalExpenses;

  const profitClass = netProfit > 0 ? 'net-profit-positive' : netProfit < 0 ? 'net-profit-negative' : 'net-profit-zero';
  const profitLabel = netProfit > 0 ? t.positive : netProfit < 0 ? t.negative : t.breakeven;

  // ── Report payload: static accounting block (works without running the AI) ──
  const lastAccountingSigRef = useRef('');

  useEffect(() => {
    if (!setReportData) return;
    if (loading) return;
    if (!nodes || nodes.length === 0) return;   // "no data" == no nodes, never a 0 total

    // Reuse the exact same recursive roll-up the on-screen totals use.
    const nodeTotal = (node) => computeBranchTotal([node]);
    const nodeName = (node) => (lang === 'ar'
      ? (node.category_name_ar || node.custom_name || '')
      : (node.category_name_en || node.custom_name || ''));

    const buildBreakdown = (roots, denomTotal, pctKey) => {
      const rows = roots
        .map(n => ({ name: String(nodeName(n) || ''), amount: Number(nodeTotal(n)) || 0 }))
        .sort((a, b) => b.amount - a.amount);
      const head = rows.slice(0, 8);
      const tail = rows.slice(8);
      if (tail.length > 0) {
        head.push({
          name: lang === 'ar' ? 'أخرى' : 'Other',
          amount: tail.reduce((sum, r) => sum + r.amount, 0)
        });
      }
      return head.map(r => ({
        name: r.name,
        amount: r.amount,
        [pctKey]: denomTotal > 0 ? Math.round((r.amount / denomTotal) * 1000) / 10 : 0,
        pct_of_revenue: totalRevenue > 0 ? Math.round((r.amount / totalRevenue) * 1000) / 10 : 0
      }));
    };

    const revenue_breakdown = buildBreakdown(rootRevenues, totalRevenue, 'pct_of_revenue');
    const expense_breakdown = buildBreakdown(rootExpenses, totalExpenses, 'pct_of_expenses');

    const summary = {
      fiscal_year: Number(selectedYear),
      currency: lang === 'ar' ? 'ج.م' : 'EGP',
      total_revenue: Number(totalRevenue) || 0,
      total_expenses: Number(totalExpenses) || 0,
      net_profit: Number(netProfit) || 0,
      profit_margin: totalRevenue > 0 ? Math.round((netProfit / totalRevenue) * 1000) / 10 : 0,
      expense_ratio: totalRevenue > 0 ? Math.round((totalExpenses / totalRevenue) * 1000) / 10 : 0,
      revenue_item_count: nodes.filter(n => n.node_type === 'revenue').length,
      expense_item_count: nodes.filter(n => n.node_type === 'expense').length,
      verdict: netProfit > 0 ? 'profit' : netProfit < 0 ? 'loss' : 'breakeven'
    };

    const sig = JSON.stringify([summary, revenue_breakdown, expense_breakdown]);
    if (sig === lastAccountingSigRef.current) return;   // avoid redundant global updates
    lastAccountingSigRef.current = sig;

    commitModule(setReportData, 'accounting', {
      accounting_summary: summary,
      accounting_revenue_breakdown: revenue_breakdown,
      accounting_expense_breakdown: expense_breakdown
    }, true, null);
  }, [nodes, totalRevenue, totalExpenses, netProfit, selectedYear, lang, loading,
      rootRevenues, rootExpenses, computeBranchTotal, setReportData]);

  // ── CRUD handlers ─────────────────────────────────────────────────────
  const debouncedSave = useDebouncedCallback(async (nodeId, updates, callback) => {
    try {
      await ApiService.put(`/accounting/node/${nodeId}`, { user_id: userId, ...updates });
      callback?.(true);
    } catch (e) {
      console.error('Save error:', e);
      callback?.(false);
    }
  }, 500);

  const handleUpdateNode = useCallback((nodeId, updates, callback) => {
    // Optimistic update
    setNodes(prev => prev.map(n => {
      if (n.id === nodeId) return { ...n, ...updates };
      return n;
    }));
    // Debounced save
    debouncedSave(nodeId, nodeId, updates, callback);
  }, [debouncedSave]);

  const handleAddNode = useCallback(async (parentId, nodeType) => {
    try {
      const newNode = await ApiService.post('/accounting/node', {
        user_id: userId,
        parent_id: parentId || null,
        node_type: nodeType,
        fiscal_year: selectedYear,
        total_amount: 0,
      });
      setNodes(prev => [...prev, newNode]);
    } catch (e) {
      console.error('Add node error:', e);
    }
  }, [userId, selectedYear]);

  const handleDeleteNode = useCallback(async (nodeId) => {
    // Optimistic: remove the node and all descendants
    const getDescendantIds = (id) => {
      const children = nodes.filter(n => n.parent_id === id);
      return [id, ...children.flatMap(c => getDescendantIds(c.id))];
    };
    const idsToRemove = new Set(getDescendantIds(nodeId));
    setNodes(prev => prev.filter(n => !idsToRemove.has(n.id)));

    try {
      await ApiService.delete(`/accounting/node/${nodeId}?user_id=${userId}`);
    } catch (e) {
      console.error('Delete error:', e);
      loadTree(); // Revert on failure
    }
  }, [nodes, userId, loadTree]);

  const handleCategorySelect = useCallback(async (nodeId, category) => {
    const updates = {
      category_id: category.id,
      custom_name: null,
    };
    setNodes(prev => prev.map(n => {
      if (n.id === nodeId) return {
        ...n,
        ...updates,
        category_name_en: category.name_en,
        category_name_ar: category.name_ar,
      };
      return n;
    }));
    try {
      await ApiService.put(`/accounting/node/${nodeId}`, { user_id: userId, ...updates });
    } catch (e) {
      console.error('Category update error:', e);
    }
  }, [userId]);

  // ── AI Advisor ────────────────────────────────────────────────────────
  const handleAIAnalyze = useCallback(async () => {
    if (nodes.length === 0) return;
    setAiStreaming(true);
    setAiResponse('');
    setAiOpen(true);

    try {
      // 1. Strict Data Aggregation
      const getNodeTotal = (nodeId) => {
        const children = nodes.filter(n => n.parent_id === nodeId);
        if (children.length === 0) {
          const node = nodes.find(n => n.id === nodeId);
          return node?.total_amount || 0;
        }
        return children.reduce((sum, c) => sum + getNodeTotal(c.id), 0);
      };

      const treeDataWithTotals = nodes.map(n => ({
        ...n,
        computed_total: getNodeTotal(n.id)
      }));

      aiAbortControllerRef.current = new AbortController();

      const response = await fetch(`${BASE_URL}/accounting/ai-advisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: aiAbortControllerRef.current.signal,
        body: JSON.stringify({
          user_id: userId,
          fiscal_year: selectedYear,
          tree_data: treeDataWithTotals,
          calculated_total_revenue: totalRevenue,
          calculated_total_expenses: totalExpenses,
          calculated_net_profit: netProfit,
          lang: lang,
          length: aiLength,
        }),
      });

      if (!response.ok) throw new Error(`API Error (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let accumulatedText = '';
      let accumulatedThinking = '';
      let buffer = '';
      let inThinking = false;

      setAiIsThinking(false);
      setAiThinking('');
      setAiResponse('');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;

        buffer += decoder.decode(value, { stream: true });
        
        // Split on SSE message boundary
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || ''; // Keep incomplete fragment

        for (const part of parts) {
          const lines = part.split('\n').filter(l => l.trim());
          for (let line of lines) {
            if (line.startsWith('data: ')) line = line.slice(6);
            if (line === '[DONE]') continue;

            let data;
            try {
              data = JSON.parse(line);
            } catch (err) {
              continue; // Skip malformed fragment
            }

            if (data.error) {
              const errMsg = data.error.message || data.error || 'Unknown error';
              setAiResponse(prev => prev + `\n\n**Error**: ${errMsg}`);
              continue;
            }

            let tokenContent = '';
            let tokenReasoning = '';

            const delta = data.choices?.[0]?.delta;
            if (delta) {
              if (delta.content !== undefined) tokenContent = delta.content;
              if (delta.reasoning !== undefined) tokenReasoning = delta.reasoning;
            } else if (data.message?.content !== undefined) {
              tokenContent = data.message.content;
            }

            if (!tokenContent && !tokenReasoning) continue;

            if (tokenReasoning) {
              setAiIsThinking(true);
              accumulatedThinking += tokenReasoning;
              setAiThinking(accumulatedThinking);
            }

            // Handle <think> tags natively embedded in the content stream.
            //
            // `inThinking` is a LOCAL let, not React state. setAiIsThinking()
            // cannot update the const this async closure captured at render
            // time, so a state read here stays false for the entire stream and
            // every reasoning token is appended to accumulatedText - which is
            // committed verbatim as accounting_ai_text and printed in the
            // customer's PDF. Same idiom as IoTView and CropRecommendView.
            if (tokenContent) {
              if (tokenContent.includes('<think>')) {
                inThinking = true;
                setAiIsThinking(true);
                const parts = tokenContent.split('<think>');
                if (parts[0]) {
                  accumulatedText += parts[0];
                  setAiResponse(accumulatedText);
                }
                tokenContent = parts.slice(1).join('<think>');
              }
              if (inThinking && tokenContent.includes('</think>')) {
                inThinking = false;
                setAiIsThinking(false);
                const parts = tokenContent.split('</think>');
                if (parts[0]) {
                  accumulatedThinking += parts[0];
                  setAiThinking(accumulatedThinking);
                }
                tokenContent = parts.slice(1).join('</think>');
              }
              if (inThinking) {
                if (tokenContent) {
                  accumulatedThinking += tokenContent;
                  setAiThinking(accumulatedThinking);
                }
              } else if (tokenContent) {
                accumulatedText += tokenContent;
                setAiResponse(accumulatedText);
              }
            }
          }
        }
      }

      // Save report to database upon stream completion
      if (accumulatedText) {
        // Report payload: AI insight text, taken from the LOCAL accumulator (never aiResponse state).
        if (setReportData) {
          commitModule(setReportData, 'accounting', { accounting_ai_text: accumulatedText }, true, null);
        }
        try {
          await ApiService.post('/accounting/reports', {
            user_id: userId,
            fiscal_year: selectedYear,
            report_content: accumulatedText,
            word_count_preference: aiLength
          });
        } catch (saveErr) {
          console.error('Failed to save AI report history:', saveErr);
        }
      }

    } catch (e) {
      console.error('AI analysis error:', e);
      setAiResponse(t.aiError || 'AI analysis failed.');
    } finally {
      setAiStreaming(false);
    }
  }, [nodes, userId, selectedYear, lang, aiLength, BASE_URL, t.aiError, totalRevenue, totalExpenses, netProfit, setReportData]);

  const loadReportHistory = useCallback(async () => {
    setHistoryOpen(true);
    setLoadingHistory(true);
    try {
      const data = await ApiService.get(`/accounting/reports/${userId}`);
      setReportHistory(data || []);
    } catch (e) {
      console.error('Failed to load history', e);
    } finally {
      setLoadingHistory(false);
    }
  }, [userId]);

  // ── Year options ──────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);

  // ── Render ────────────────────────────────────────────────────────────
  const hasData = nodes.length > 0;

  return (
    <div className="flex flex-col h-full bg-[#F4EFEA] dark:bg-[#0A0F1E] rounded-2xl overflow-hidden shadow-inner border border-black/5 dark:border-white/5 relative" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <div className="accounting-topbar">
        <div className="flex flex-col items-end gap-2 mt-2 md:mt-0">
          {/* Year Selector */}
          <div className="flex items-center gap-2 drop-shadow-sm text-slate-900 dark:text-white">
            <span className="text-[10px] font-semibold opacity-70 uppercase tracking-wide">{t.yearLabel || (lang === 'ar' ? 'السنة' : 'Year')}</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="bg-transparent outline-none font-bold text-xs cursor-pointer"
            >
              {yearOptions.map(y => <option key={y} value={y} className="dark:bg-[#1B263B] dark:text-white">{y}</option>)}
            </select>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadReportHistory}
            className="flex items-center gap-1.5 transition-all font-bold text-xs text-slate-900 dark:text-white drop-shadow-sm hover:opacity-70"
          >
            <History size={14} />
            <span className="hidden md:inline">{t.history || (lang === 'ar' ? 'سجل التقارير' : 'Reports History')}</span>
          </motion.button>

          {/* AI Advisor Toggle */}
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setAiOpen(!aiOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white text-[10px] font-semibold shadow-[0_4px_12px_rgba(196,122,44,0.35)] hover:shadow-[0_6px_20px_rgba(196,122,44,0.45)] transition-all"
          >
            <Sparkles size={12} />
            {t.aiAdvisor || 'AI Advisor'}
          </motion.button>
        </div>
      </div>

      {/* ── Main Canvas ─────────────────────────────────────────────────── */}
      <div className="accounting-canvas">
        {loading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin text-[#C47A2C]" />
          </div>
        ) : !hasData ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center gap-4 max-w-md"
          >
            <div className={`net-profit-node net-profit-zero`}>
              <Calculator size={40} className="text-amber-500/60 mb-1" />
              <span className="text-2xl font-bold tabular-nums">0 {t.currencySymbol}</span>
              <span className="text-xs font-semibold opacity-50 mt-1">{t.netProfit}</span>
            </div>
            <h2 className="text-xl font-bold mt-4">{t.emptyTree}</h2>
            <p className="text-sm opacity-60">{t.emptyTreeDesc}</p>
            <div className="flex gap-3 mt-2">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleAddNode(null, 'revenue')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold text-sm border border-emerald-500/20 hover:bg-emerald-500/15 transition-all"
              >
                <Plus size={16} /> {t.revenues}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleAddNode(null, 'expense')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 font-semibold text-sm border border-red-500/20 hover:bg-red-500/15 transition-all"
              >
                <Plus size={16} /> {t.expenses}
              </motion.button>
            </div>
          </motion.div>
        ) : (
          /* Tree Layout: Expenses | Net Profit | Revenues */
          <div className="flex items-start gap-6 w-full max-w-[1600px] justify-center">

            {/* ── LEFT BRANCH (Expenses in LTR / Revenues in RTL) ── */}
            <div className="tree-branch flex-1 overflow-y-auto max-h-[calc(100vh-10rem)]">
              <div className="sticky top-0 z-10 bg-[#F4EFEA] dark:bg-[#0A0F1E] flex items-center gap-2 mb-3 pt-2 pb-2 border-b border-black/5 dark:border-white/5">
                <h3 className="text-sm font-bold uppercase tracking-wider opacity-60">
                  {isRTL ? t.revenues : t.expenses}
                </h3>
                <span className={`acc-amount text-sm ${isRTL ? 'acc-amount-revenue' : 'acc-amount-expense'}`}>
                  {formatAmount(isRTL ? totalRevenue : totalExpenses, t)}
                </span>
                <button
                  onClick={() => handleAddNode(null, isRTL ? 'revenue' : 'expense')}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-[#C47A2C] ms-auto"
                  title={t.addNode}
                >
                  <Plus size={16} />
                </button>
              </div>
              <AnimatePresence>
                {(isRTL ? rootRevenues : rootExpenses).map(node => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    children={nodes.filter(n => n.parent_id === node.id)}
                    lang={lang}
                    t={t}
                    onUpdate={handleUpdateNode}
                    onDelete={handleDeleteNode}
                    onAddChild={handleAddNode}
                    onCategorySelect={handleCategorySelect}
                    allNodes={nodes}
                    hoveredPath={hoveredPath}
                    setHoveredPath={setHoveredPath}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* ── CENTER: Net Profit Node ── */}
            <div className="flex flex-col items-center flex-shrink-0 pt-8">
              <motion.div
                key={netProfit}
                className={`net-profit-node ${profitClass}`}
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                <span className="text-[0.65rem] font-bold uppercase tracking-widest opacity-50 mb-1">
                  {t.netProfit}
                </span>
                <motion.span
                  key={netProfit}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-2xl font-extrabold tabular-nums"
                >
                  {formatAmount(netProfit, t)}
                </motion.span>
                <span className="text-[0.6rem] font-semibold mt-1 opacity-60">{profitLabel}</span>
              </motion.div>

              {/* Summary chips below */}
              <div className="flex gap-3 mt-4">
                <div className="text-center">
                  <span className="text-[0.6rem] font-bold uppercase tracking-wider opacity-40 block">{t.revenues}</span>
                  <span className="acc-amount acc-amount-revenue text-xs">{formatAmount(totalRevenue, t)}</span>
                </div>
                <div className="w-px bg-black/10 dark:bg-white/10"></div>
                <div className="text-center">
                  <span className="text-[0.6rem] font-bold uppercase tracking-wider opacity-40 block">{t.expenses}</span>
                  <span className="acc-amount acc-amount-expense text-xs">{formatAmount(totalExpenses, t)}</span>
                </div>
              </div>
            </div>

            {/* ── RIGHT BRANCH (Revenues in LTR / Expenses in RTL) ── */}
            <div className="tree-branch flex-1 overflow-y-auto max-h-[calc(100vh-10rem)]">
              <div className="sticky top-0 z-10 bg-[#F4EFEA] dark:bg-[#0A0F1E] flex items-center gap-2 mb-3 pt-2 pb-2 border-b border-black/5 dark:border-white/5">
                <h3 className="text-sm font-bold uppercase tracking-wider opacity-60">
                  {isRTL ? t.expenses : t.revenues}
                </h3>
                <span className={`acc-amount text-sm ${isRTL ? 'acc-amount-expense' : 'acc-amount-revenue'}`}>
                  {formatAmount(isRTL ? totalExpenses : totalRevenue, t)}
                </span>
                <button
                  onClick={() => handleAddNode(null, isRTL ? 'expense' : 'revenue')}
                  className="p-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-[#C47A2C] ms-auto"
                  title={t.addNode}
                >
                  <Plus size={16} />
                </button>
              </div>
              <AnimatePresence>
                {(isRTL ? rootExpenses : rootRevenues).map(node => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    children={nodes.filter(n => n.parent_id === node.id)}
                    lang={lang}
                    t={t}
                    onUpdate={handleUpdateNode}
                    onDelete={handleDeleteNode}
                    onAddChild={handleAddNode}
                    onCategorySelect={handleCategorySelect}
                    allNodes={nodes}
                    hoveredPath={hoveredPath}
                    setHoveredPath={setHoveredPath}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* ── AI Advisor Panel ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {aiOpen && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="ai-advisor-panel"
          >
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-black/5 dark:border-white/5 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[#C47A2C]" />
                <h3 className="font-bold text-sm">{t.aiAdvisor}</h3>
                <span className="text-xs opacity-50">— {t.aiAdvisorDesc}</span>
              </div>
              <div className="flex items-center gap-3">
                {/* Length selector */}
                <div className="flex items-center gap-1 bg-black/5 dark:bg-white/5 rounded-xl p-0.5">
                  {[
                    { key: 'short', label: t.aiShort || 'Short' },
                    { key: 'medium', label: t.aiMedium || 'Medium' },
                    { key: 'full', label: t.aiFull || 'Full' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setAiLength(opt.key)}
                      className={`text-[0.65rem] font-semibold px-3 py-1 rounded-lg transition-all ${
                        aiLength === opt.key
                          ? 'bg-[#C47A2C] text-white shadow-sm'
                          : 'hover:bg-black/5 dark:hover:bg-white/5 opacity-60'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Analyze button */}
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleAIAnalyze}
                  disabled={aiStreaming || nodes.length === 0}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white text-xs font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {aiStreaming ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {aiStreaming ? (t.aiAnalyzing || 'Analyzing...') : (t.aiAnalyze || 'Analyze')}
                </motion.button>

                {/* Close */}
                <button
                  onClick={() => {
                    if (aiAbortControllerRef.current) {
                      aiAbortControllerRef.current.abort();
                    }
                    setAiOpen(false);
                  }}
                  className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
              {(aiResponse || aiThinking) ? (
                <div dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                  
                  {/* Thinking Block */}
                  <AnimatePresence>
                    {(aiIsThinking || aiThinking) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mb-4 bg-black/5 dark:bg-white/5 rounded-xl border border-black/10 dark:border-white/10 overflow-hidden"
                      >
                        <div className="px-4 py-2 bg-black/5 dark:bg-white/5 border-b border-black/5 dark:border-white/5 flex items-center gap-2">
                          {aiIsThinking ? (
                            <Loader2 size={14} className="animate-spin text-[#C47A2C]" />
                          ) : (
                            <Check size={14} className="text-[#C47A2C]" />
                          )}
                          <span className="text-xs font-semibold opacity-70">
                            {aiIsThinking ? (t.aiThinking || 'Thinking...') : (t.aiThought || 'Thought Process')}
                          </span>
                        </div>
                        <div className="p-4 text-sm opacity-80 whitespace-pre-wrap font-mono">
                          {aiThinking}
                          {aiIsThinking && <span className="inline-block w-1.5 h-3 bg-[#C47A2C] ml-1 animate-pulse" />}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Main Content Block */}
                  {aiResponse && (
                    <div className="mt-4">
                      <MarkdownText text={aiResponse} />
                    </div>
                  )}
                  {aiStreaming && !aiIsThinking && (
                    <span className="inline-block w-2 h-4 bg-[#C47A2C] rounded-sm animate-pulse ml-1 mt-2" />
                  )}
                </div>
              ) : (
                <div className="text-center py-8 opacity-40">
                  <Sparkles size={32} className="mx-auto mb-3 text-[#C47A2C]/40" />
                  <p className="text-sm">{nodes.length === 0 ? (t.aiEmpty || 'Add data first') : (t.aiAdvisorDesc || 'Click analyze to get insights')}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Reports History Modal ── */}
      {createPortal(
        <AnimatePresence>
          {historyOpen && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="bg-[#F4EFEA] dark:bg-[#0A0F1E] text-slate-900 dark:text-white w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl border border-black/10 dark:border-white/10"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5">
                  <div className="flex items-center gap-2">
                    <History size={20} className="text-[#C47A2C]" />
                    <h2 className="text-lg font-bold">{t.history || (lang === 'ar' ? 'سجل التقارير' : 'Reports History')}</h2>
                  </div>
                  <button onClick={() => setHistoryOpen(false)} className="p-2 hover:bg-black/10 dark:hover:bg-white/10 rounded-full transition-colors">
                    <X size={20} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center h-40">
                      <Loader2 size={32} className="animate-spin text-[#C47A2C]" />
                    </div>
                  ) : reportHistory.length === 0 ? (
                    <div className="text-center text-black/50 dark:text-white/50 py-12">
                      {t.noHistory || (lang === 'ar' ? 'لم يتم العثور على تقارير سابقة.' : 'No previous reports found.')}
                    </div>
                  ) : (
                    reportHistory.map(report => (
                      <div key={report.id} className="bg-white/50 dark:bg-black/20 rounded-2xl p-6 border border-black/5 dark:border-white/5 shadow-sm">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b border-black/5 dark:border-white/5">
                          <span className="font-bold text-sm bg-black/5 dark:bg-white/10 px-3 py-1 rounded-full">
                            {t.yearLabel || (lang === 'ar' ? 'السنة' : 'Year')}: {report.fiscal_year}
                          </span>
                          <span className="text-xs opacity-60">
                            {new Date(report.created_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US')}
                          </span>
                        </div>
                        <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0">
                          <MarkdownText text={report.report_content} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
